import { useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import SEO from "@/components/feature/SEO";
import { webpageSchema, breadcrumbSchema, siteUrl } from "@/lib/schemas";
import { TrendingUp, IndianRupee, Calendar, Percent, Download, ArrowRight } from "lucide-react";

export default function SIPCalculatorPage() {
  const baseUrl = siteUrl;
  const pageUrl = `${baseUrl}/calculator/sip`;

  const schemas = [
    webpageSchema(
      "SIP Calculator | Mutual Fund Returns & Wealth Planner | ExpertAdvise.in",
      "Calculate mutual fund SIP returns and projected wealth. Estimate monthly investment growth with compounding and step-up SIP options. Free online calculator.",
      pageUrl
    ),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      { name: "Calculators", url: `${baseUrl}/calculators` },
      { name: "SIP Calculator", url: pageUrl },
    ]),
  ];

  const [monthlyInvestment, setMonthlyInvestment] = useState(5000);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [duration, setDuration] = useState(15);
  const [stepUp, setStepUp] = useState(0);

  const results = useMemo(() => {
    let totalInvested = 0;
    let corpus = 0;
    const monthlyRate = expectedReturn / 100 / 12;
    let currentSIP = monthlyInvestment;
    const yearlyData = [];

    for (let year = 1; year <= duration; year++) {
      for (let month = 1; month <= 12; month++) {
        corpus = (corpus + currentSIP) * (1 + monthlyRate);
        totalInvested += currentSIP;
      }
      yearlyData.push({
        year,
        invested: Math.round(totalInvested),
        corpus: Math.round(corpus),
      });
      currentSIP = currentSIP * (1 + stepUp / 100);
    }

    const wealthGained = corpus - totalInvested;
    return { totalInvested: Math.round(totalInvested), corpus: Math.round(corpus), wealthGained: Math.round(wealthGained), yearlyData };
  }, [monthlyInvestment, expectedReturn, duration, stepUp]);

  const formatCurrency = useCallback((val: number) => {
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(2)} Lakhs`;
    return `₹${val.toLocaleString()}`;
  }, []);

  const handleDownload = () => {
    alert("PDF report generation will be enabled after backend integration.");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="SIP Calculator | Mutual Fund Returns & Wealth Planner | ExpertAdvise.in"
        description="Calculate mutual fund SIP returns and projected wealth. Estimate monthly investment growth with compounding and step-up SIP options. Free online calculator."
        canonical={pageUrl}
        ogUrl={pageUrl}
        schemas={schemas}
      />
      <Navbar />

      <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block bg-emerald-50 text-emerald-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
              Investment Calculator
            </span>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">
              SIP Calculator
            </h1>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed">
              Calculate how much wealth you can create with Systematic Investment Plans. 
              See the power of compounding and rupee cost averaging over time.
            </p>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            {/* Inputs */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-5">Investment Details</h3>

                <div className="space-y-5">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                      <IndianRupee className="w-4 h-4 text-primary-500" />
                      Monthly Investment
                    </label>
                    <input
                      type="range"
                      min="500"
                      max="100000"
                      step="500"
                      value={monthlyInvestment}
                      onChange={(e) => setMonthlyInvestment(Number(e.target.value))}
                      className="w-full accent-primary-600 mb-2"
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">₹500</span>
                      <input
                        type="number"
                        value={monthlyInvestment}
                        onChange={(e) => setMonthlyInvestment(Number(e.target.value))}
                        className="flex-1 text-center bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm font-semibold text-slate-900 outline-none focus:border-primary-400"
                      />
                      <span className="text-sm text-slate-500">₹1L</span>
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                      <Percent className="w-4 h-4 text-primary-500" />
                      Expected Annual Return
                    </label>
                    <input
                      type="range"
                      min="6"
                      max="20"
                      step="0.5"
                      value={expectedReturn}
                      onChange={(e) => setExpectedReturn(Number(e.target.value))}
                      className="w-full accent-primary-600 mb-2"
                    />
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span>6%</span>
                      <span className="font-semibold text-primary-700">{expectedReturn}%</span>
                      <span>20%</span>
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                      <Calendar className="w-4 h-4 text-primary-500" />
                      Investment Duration (Years)
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="30"
                      step="1"
                      value={duration}
                      onChange={(e) => setDuration(Number(e.target.value))}
                      className="w-full accent-primary-600 mb-2"
                    />
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span>1 Year</span>
                      <span className="font-semibold text-primary-700">{duration} Years</span>
                      <span>30 Years</span>
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                      <TrendingUp className="w-4 h-4 text-primary-500" />
                      Annual Step-Up (%)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="20"
                      step="1"
                      value={stepUp}
                      onChange={(e) => setStepUp(Number(e.target.value))}
                      className="w-full accent-primary-600 mb-2"
                    />
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span>0%</span>
                      <span className="font-semibold text-primary-700">{stepUp}%</span>
                      <span>20%</span>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={handleDownload}
                className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-5 py-3 rounded-xl transition-colors"
              >
                <Download className="w-4 h-4" />
                Download PDF Report
              </button>
            </div>

            {/* Results */}
            <div className="lg:col-span-2 space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-2xl border border-slate-200 p-5 text-center">
                  <div className="text-xs text-slate-500 mb-1">Total Invested</div>
                  <div className="text-xl md:text-2xl font-bold text-slate-900">{formatCurrency(results.totalInvested)}</div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 p-5 text-center">
                  <div className="text-xs text-slate-500 mb-1">Wealth Gained</div>
                  <div className="text-xl md:text-2xl font-bold text-emerald-600">{formatCurrency(results.wealthGained)}</div>
                </div>
                <div className="bg-primary-600 rounded-2xl p-5 text-center">
                  <div className="text-xs text-white/80 mb-1">Final Corpus</div>
                  <div className="text-xl md:text-2xl font-bold text-white">{formatCurrency(results.corpus)}</div>
                </div>
              </div>

              {/* Yearly Table */}
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="p-5 border-b border-slate-100">
                  <h3 className="text-lg font-bold text-slate-900">Year-by-Year Projection</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Year</th>
                        <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Amount Invested</th>
                        <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Corpus Value</th>
                        <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Wealth Gained</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.yearlyData.map((row) => (
                        <tr key={row.year} className="border-t border-slate-50 hover:bg-slate-50/50">
                          <td className="px-5 py-3 font-medium text-slate-900">Year {row.year}</td>
                          <td className="px-5 py-3 text-right text-slate-600">{formatCurrency(row.invested)}</td>
                          <td className="px-5 py-3 text-right font-semibold text-primary-700">{formatCurrency(row.corpus)}</td>
                          <td className="px-5 py-3 text-right text-emerald-600 font-medium">{formatCurrency(row.corpus - row.invested)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 bg-primary-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">
            Start Your SIP Today
          </h2>
          <p className="text-slate-600 mb-6">
            Ready to turn these projections into reality? Our advisors will help you choose the right funds and start your first SIP.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/book-appointment?source=SIP%20Calculator%20Page"
              className="bg-cta-600 hover:bg-cta-700 text-white font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap"
            >
              Book Free Consultation
            </Link>
            <Link
              to="/mutual-funds"
              className="flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-700 font-semibold px-6 py-3 rounded-full border border-slate-200 transition-colors whitespace-nowrap"
            >
              Explore Mutual Funds
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}